import React, { Fragment, useCallback, useMemo, useRef, useState } from 'react';

import {
    Alert,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    InputLabel,
    MenuItem,
    Select,
    type SelectChangeEvent,
    TextField
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import OverrideControls from './OverrideControls';
import SuperAdminTools from './SuperAdminTools';
import {
    type ClassAccess,
    ClassLevelOverrideOptions,
    type ClassOptionsOverrides,
    type ScreenShareResolutions
} from '../../../types/adminConfig';
import { ConfigIngestModes, type Permission } from '../../../types/recordingTypes';
import AdminAuthContainer from '../AdminAuthContainer';
import AdminSettingsContainer from '../AdminSettingsContainer';
import SaveButton from '../components/SaveButton';
import StyledFormTitle from '../styles/form-title';
import { AdminControlCheckbox } from '../utils/AdminControlCheckbox';

const Title = styled(StyledFormTitle)`
    margin-bottom: 1rem;
`;

const StyledFormGroup = styled(FormGroup)`
    max-width: 400px;
`;

const StyledSelect = styled(Select)`
    width: 400px;
`;

const StyledFormGroupContainer = styled.div`
    margin-left: 10px;
    margin-bottom: 1rem;
`;

const Spacer = styled.div`
    margin: 1rem;
`;

type FeatureFlag = {
    name: string;
    label: string;
    condition?: boolean;
    options?: boolean;
    overrideName?: keyof ClassOptionsOverrides;
    hint?: string;
    validation?: (newVal: any) => string | void;
};

const SuperAdmin = () => {
    const { t } = useTranslation('admin_settings');
    const { isSuperAdmin } = useContainer(AdminAuthContainer);
    const {
        adminSettings,
        setAdminSettings,
        isSavingSettingsData,
        handleSaveSettings,
        setImportRecordings,
        school,
        isSavingDisabled
    } = useContainer(AdminSettingsContainer);
    const { featureSettings, authSettings, classForTeamsSettings } = adminSettings;
    const { options } = featureSettings;
    const [errors, setErrors] = useState<Record<string, string>>({});

    const isTeams = school?.isTeams;
    const isManagedZoom = school?.isManagedZoom;
    const hasAzurePhoneNumbers = adminSettings.classForTeamsSettings.hasAzurePhoneNumbers;

    const handleFeatureSettingsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { id, checked } = event.target;

        setAdminSettings((prev) => ({
            ...prev,
            featureSettings: {
                ...prev.featureSettings,
                [id]: checked
            }
        }));
    };

    const handleUpdateOverrides = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;

        setAdminSettings((prev) => ({
            ...prev,
            featureSettings: {
                ...prev.featureSettings,
                overrides: {
                    ...prev.featureSettings.overrides,
                    [name]: value
                }
            }
        }));
    };

    const handleUpdateOptionsBool = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { id, name, checked } = event.target;
        const updatedOptions = { ...options, [id]: checked };

        if (!checked) {
            if (name === 'hasNextGenChat') {
                updatedOptions.hasNextGenChatActivityFeed = false;
            } 
            
            if (name === 'offerBreakoutTranscripts') {
                updatedOptions.hasAllBreakoutTranscripts = false;
            }

            if (name === 'hasTeamsLiveCaptioning') {
                updatedOptions.offerBreakoutTranscripts = false;
                updatedOptions.hasAllBreakoutTranscripts = false;
            }
        }

        setAdminSettings((prev) => ({
            ...prev,
            featureSettings: {
                ...prev.featureSettings,
                options: updatedOptions
            }
        }));
    };

    const handleSelectSettingsChange = (
        event: SelectChangeEvent<ClassAccess | Permission | ConfigIngestModes | ScreenShareResolutions>
    ) => {
        const { name, value } = event.target;

        setAdminSettings((prev) => ({
            ...prev,
            featureSettings: {
                ...prev.featureSettings,
                [name]: value
            }
        }));
    };

    const handleImportRecordingsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setImportRecordings(event.target.checked);
    };

    const handleDomainsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const key = event.target.id || event.target.name;
        const value = event.target.value
            .split(',')
            .map((d) => d.trim().toLowerCase())
            .filter(Boolean); // Optional: remove empty strings

        setAdminSettings((prev) => ({
            ...prev,
            authSettings: {
                ...prev.authSettings,
                [key]: value
            }
        }));
    };

    const handleClassForTeamsSettingsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const key = event.target.id || event.target.name;
        const value = Number(event.target.value);

        setAdminSettings((prev) => ({
            ...prev,
            classForTeamsSettings: {
                ...prev.classForTeamsSettings,
                [key]: value
            }
        }));
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const key = event.target.id || event.target.name;
        const value = event.target.value;

        setAdminSettings((prev) => ({
            ...prev,
            authSettings: {
                ...prev.authSettings,
                [key]: value
            }
        }));
    };

    const featureFlags: FeatureFlag[] = useMemo(
        () => [
            {
                name: 'hasTeamsLiveCaptioning',
                label: 'configuration.hasTeamsLiveCaptioning',
                options: true,
                condition: isTeams
            },
            {
                name: 'offerBreakoutTranscripts',
                label: 'configuration.offerBreakoutTranscripts',
                options: true
            },
            {
                name: 'disableTeamsRecording',
                label: 'configuration.recordings.disable',
                options: true,
                condition: isTeams
            },
            {
                name: 'subClasses',
                label: 'configuration.allowSubClassesInfo',
                hint: 'configuration.allowSubClassesHint',
                options: true
            },
            {
                name: 'hasNextGenLearningToolSidebar',
                label: 'configuration.enable-next-gen-learning-tool-sidebar',
                options: true
            },
            {
                name: 'hasConfigurableUI',
                label: 'configuration.has-configurable-user-interface',
                options: true
            },
            {
                name: 'showAdvancedFollowMeOptions',
                label: 'configuration.show-advanced-follow-me-options',
                options: true
            },
            {
                name: 'enableACS25Videos',
                label: 'configuration.enableACS25Videos',
                options: true,
                condition: isTeams
            },
            {
                name: 'hasLiveTranslation',
                label: 'configuration.has-live-translation',
                options: true
            }
        ],
        [isTeams]
    );

    const featureFlagFieldRefs = useRef<Record<string, HTMLInputElement | null>>({});

    const errorCheckSave = useCallback(() => {
        const validationErrors: Record<string, string> = {};
        featureFlags.forEach((flag) => {
            if (flag.validation) {
                const error = flag.validation(featureSettings.options[flag.name]);
                if (error) {
                    validationErrors[flag.name] = error;
                }
            }
        });
        setErrors(validationErrors);
        if (Object.keys(validationErrors).length) {
            const errorFields = Object.keys(validationErrors);
            // Get error fields ordered by their position on the page, i.e. feature flag item position.
            const orderedErrorFields = featureFlags
                .filter((flag) => errorFields.some((errorField) => errorField === flag.name))
                .map((flag) => flag.name);
            if (orderedErrorFields.length) {
                // Go to first error.
                const firstError = orderedErrorFields[0];
                const field = featureFlagFieldRefs.current[firstError];
                field?.focus();
            }
            return;
        }
        handleSaveSettings();
    }, [handleSaveSettings, featureFlags, featureSettings.options]);

    const betaSettings: FeatureFlag[] = [
        {
            name: 'classNotes',
            label: t('configuration.classNotes')
        },
        {
            name: 'whiteboardScreenshotFeature',
            label: t('configuration.whiteboard-screenshot-feature')
        },
        {
            name: 'isBbData',
            label: t('configuration.is-bb-data')
        },
        {
            name: 'dataPortalCustomerSupport',
            label: t('configuration.data-portal-customer-support')
        },
        {
            name: 'allowAiForSupport',
            label: t('configuration.allow-ai-for-support')
        },
        {
            name: 'allowAiForInsights',
            label: t('configuration.allow-ai-for-insights')
        },
        {
            name: 'hasBreakoutRoomCrossCommunication',
            label: t('configuration.hasBreakoutRoomCrossCommunication'),
            options: true
        },
        {
            name: 'enableNextGenFilesGoogleServiceAccount',
            label: t('configuration.enableNextGenFilesGoogleServiceAccount'),
            hint: t('configuration.enableNextGenFilesGoogleServiceAccountHint'),
            options: true
        },
        {
            name: 'hasAiTa',
            label: t('configuration.allow-ai-ta'),
            options: true,
            overrideName: 'hasAiTa'
        },
        {
            name: 'enablePrivateDiscussion',
            label: t('configuration.enable-private-discussion'),
            options: true,
            overrideName: 'enablePrivateDiscussion'
        }
    ];

    if (!isSuperAdmin) {
        return <Alert severity="warning">{t('version-management.permissions-error')}</Alert>;
    }

    return (
        <>
            <Title>{t('navigationTabs.super-admin')}</Title>

            <StyledFormGroupContainer>
                <FormGroup>
                    {featureFlags
                        .filter((f) => f.condition ?? true)
                        .map((flag) => (
                            <Fragment key={flag.name}>
                                <AdminControlCheckbox
                                    control={
                                        <Checkbox
                                            color="primary"
                                            checked={(flag.options ? options : featureSettings)[flag.name]}
                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                if (flag.validation) {
                                                    const error = flag.validation(event.target.checked);
                                                    if (error) {
                                                        setErrors({ ...errors, [flag.name]: error });
                                                    } else {
                                                        const { [flag.name]: _, ...newErrors } = errors;
                                                        setErrors({ ...newErrors });
                                                    }
                                                }
                                                if (flag.options) {
                                                    handleUpdateOptionsBool(event);
                                                } else {
                                                    handleFeatureSettingsChange(event);
                                                }
                                            }}
                                            id={flag.name}
                                            name={flag.name}
                                            inputRef={(el) => (featureFlagFieldRefs.current[flag.name] = el)}
                                        />
                                    }
                                    label={t(flag.label)}
                                    hint={flag.hint ? t(flag.hint) : undefined}
                                    key={flag.name}
                                />
                                {errors[flag.name] && <Alert severity="error">{errors[flag.name]}</Alert>}
                                {flag.overrideName && (
                                    <OverrideControls
                                        overrideName={flag.overrideName}
                                        overrideValue={
                                            featureSettings.overrides[flag.name] ?? ClassLevelOverrideOptions.NoOne
                                        }
                                        handleUpdateOverrides={handleUpdateOverrides}
                                        labelText={t('whoCanOverride')}
                                        tooltipText={t('whoCanOverrideTooltipRMS')}
                                    />
                                )}
                            </Fragment>
                        ))}
                </FormGroup>

                <FormGroup sx={{ mt: 2 }}>
                    <FormLabel id="recordings-ingest-label" sx={{ color: 'inherit' }}>
                        {t('configuration.recordings.ingest-mode')}
                    </FormLabel>
                    <FormControl sx={{ maxWidth: 300, marginLeft: 0 }} size="small" variant="outlined">
                        <Select
                            labelId="recordings-ingest-select-label"
                            id="recordingIngestMode"
                            name="recordingIngestMode"
                            value={featureSettings.recordingIngestMode}
                            onChange={handleSelectSettingsChange}
                        >
                            <MenuItem value={ConfigIngestModes.EXCLUDE}>
                                {t('configuration.recordings.ingest-modes.exclude')}
                            </MenuItem>
                            {!isTeams && (
                                <MenuItem value={ConfigIngestModes.TRACK}>
                                    {t('configuration.recordings.ingest-modes.track')}
                                </MenuItem>
                            )}
                            <MenuItem value={ConfigIngestModes.COPY}>
                                {t(
                                    isTeams
                                        ? 'configuration.recordings.ingest-modes.copy-teams'
                                        : 'configuration.recordings.ingest-modes.copy'
                                )}
                            </MenuItem>
                            {(isTeams || isManagedZoom) && (
                                <MenuItem value={ConfigIngestModes.MOVE}>
                                    {t('configuration.recordings.ingest-modes.move')}
                                </MenuItem>
                            )}
                        </Select>
                    </FormControl>
                </FormGroup>

                <FormGroup sx={{ mb: 2, ml: 4 }}>
                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                color="primary"
                                onChange={handleImportRecordingsChange}
                                id="importRecordings"
                                name="importRecordings"
                            />
                        }
                        label={t('configuration.recordings.import')}
                    />
                </FormGroup>

                <StyledFormGroupContainer>
                    <StyledFormGroup>
                        <InputLabel htmlFor="domains">{t('auth.domains')}</InputLabel>
                        <TextField
                            value={authSettings.domains?.join(', ').trim() ?? ''}
                            onChange={handleDomainsChange}
                            id="domains"
                            name="domains"
                            helperText={t('auth.domains-helper-text')}
                            size="small"
                        />
                    </StyledFormGroup>
                </StyledFormGroupContainer>
            </StyledFormGroupContainer>

            <StyledFormGroupContainer>
                <FormGroup>
                    <InputLabel htmlFor="updateMicrosoftLicenseDays">
                        {t('configuration.update-microsoft-license')}
                    </InputLabel>
                    <TextField
                        type="number"
                        sx={{ width: '100px' }}
                        value={classForTeamsSettings.updateMicrosoftLicenseDays}
                        onChange={handleClassForTeamsSettingsChange}
                        id="updateMicrosoftLicenseDays"
                        name="updateMicrosoftLicenseDays"
                    />
                </FormGroup>
            </StyledFormGroupContainer>

            {isTeams && (
                <StyledFormGroupContainer>
                    <FormGroup>
                        <FormLabel id="select-auto-verify" sx={{ color: 'inherit' }}>
                            {t('auth.autoVerify')}
                        </FormLabel>
                        <StyledSelect
                            name="autoVerify"
                            id="autoVerify"
                            value={authSettings.autoVerify}
                            onChange={handleChange}
                        >
                            <MenuItem value={'never'}>{t('auth.autoVerifyNever')}</MenuItem>
                            {/* remove per class setting option until teams supports it */}
                            {/* <MenuItem value={'optional'}>{t('auth.autoVerifyOptional')}</MenuItem> */}
                            <MenuItem value={'always'}>{t('auth.autoVerifyAlways')}</MenuItem>
                        </StyledSelect>
                    </FormGroup>
                </StyledFormGroupContainer>
            )}

            <Title>{t('configuration.sso-rollout')}</Title>
            <StyledFormGroupContainer>
                <FormGroup>
                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                color="primary"
                                checked={featureSettings.options.hasSSOFeature}
                                onChange={handleUpdateOptionsBool}
                                id="hasSSOFeature"
                                name="hasSSOFeature"
                            />
                        }
                        label={t('configuration.hasSSOFeature')}
                    />
                </FormGroup>

                <FormGroup>
                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                color="primary"
                                checked={featureSettings.options.hasMsftAuth}
                                onChange={handleUpdateOptionsBool}
                                id="hasMsftAuth"
                                name="hasMsftAuth"
                            />
                        }
                        label={t('configuration.hasMsftAuth')}
                    />
                </FormGroup>

                <FormGroup>
                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                color="primary"
                                checked={featureSettings.options.hasClassAuth}
                                onChange={handleUpdateOptionsBool}
                                id="hasClassAuth"
                                name="hasClassAuth"
                            />
                        }
                        label={t('configuration.hasClassAuth')}
                    />
                </FormGroup>

                <FormGroup>
                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                color="primary"
                                checked={featureSettings.options.hasAuthOnClassLanding}
                                onChange={handleUpdateOptionsBool}
                                id="hasAuthOnClassLanding"
                                name="hasAuthOnClassLanding"
                            />
                        }
                        label={t('configuration.hasAuthOnClassLanding')}
                    />
                </FormGroup>

                <FormGroup>
                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                color="primary"
                                checked={featureSettings.options.hasScheduleLinkOnClassLanding}
                                onChange={handleUpdateOptionsBool}
                                id="hasScheduleLinkOnClassLanding"
                                name="hasScheduleLinkOnClassLanding"
                            />
                        }
                        label={t('configuration.hasScheduleLinkOnClassLanding')}
                    />
                </FormGroup>

                <FormGroup>
                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                color="primary"
                                checked={featureSettings.options.useIdentifierInRoster}
                                onChange={handleUpdateOptionsBool}
                                id="useIdentifierInRoster"
                                name="useIdentifierInRoster"
                            />
                        }
                        label={t('configuration.useIdentifierInRoster')}
                    />
                </FormGroup>
            </StyledFormGroupContainer>

            <Title>{t('configuration.guest-rollout')}</Title>
            <StyledFormGroupContainer>
                <FormGroup>
                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                color="primary"
                                checked={featureSettings.options.allowVerification}
                                onChange={handleUpdateOptionsBool}
                                id="allowVerification"
                                name="allowVerification"
                            />
                        }
                        label={t('configuration.allowVerification')}
                    />
                </FormGroup>
                <FormGroup>
                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                color="primary"
                                checked={featureSettings.options.allowNextGenGuests}
                                onChange={handleUpdateOptionsBool}
                                id="allowNextGenGuests"
                                name="allowNextGenGuests"
                            />
                        }
                        label={t('configuration.allowNextGenGuests')}
                    />
                </FormGroup>
            </StyledFormGroupContainer>

            <Title>{t('beta-settings')}</Title>
            <StyledFormGroupContainer>
                {betaSettings.map(({ name, label, hint, options, overrideName }) => {
                    const checked = options ? featureSettings.options?.[name] : featureSettings[name];

                    const onChangeHandler = options ? handleUpdateOptionsBool : handleFeatureSettingsChange;

                    return (
                        <FormGroup key={name}>
                            {'hint' in (hint ? { hint } : {}) ? (
                                <AdminControlCheckbox
                                    control={
                                        <Checkbox
                                            color="primary"
                                            checked={checked}
                                            onChange={onChangeHandler}
                                            id={name}
                                            name={name}
                                        />
                                    }
                                    label={label}
                                    hint={hint}
                                />
                            ) : (
                                <FormControlLabel
                                    control={
                                        <Checkbox checked={checked} onChange={onChangeHandler} id={name} name={name} />
                                    }
                                    label={label}
                                />
                            )}

                            {overrideName && (
                                <OverrideControls
                                    overrideName={overrideName}
                                    overrideValue={featureSettings.overrides?.[name] ?? ClassLevelOverrideOptions.NoOne}
                                    handleUpdateOverrides={handleUpdateOverrides}
                                    labelText={t('whoCanOverride')}
                                    tooltipText={t('whoCanOverrideTooltip')}
                                />
                            )}
                        </FormGroup>
                    );
                })}
            </StyledFormGroupContainer>

            <Title>{t('deprecated-settings')}</Title>
            <StyledFormGroupContainer>
                <StyledFormGroupContainer>
                    {!school?.isCollab && (
                        <FormGroup>
                            <AdminControlCheckbox
                                control={
                                    <Checkbox
                                        color="primary"
                                        checked={featureSettings.options.salesTrainingMode}
                                        onChange={handleUpdateOptionsBool}
                                        id="salesTrainingMode"
                                        name="salesTrainingMode"
                                    />
                                }
                                label={t('configuration.sales-training-mode')}
                            />
                        </FormGroup>
                    )}
                    <FormGroup>
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={featureSettings.options.hasSyncSeatingCharts}
                                    onChange={handleUpdateOptionsBool}
                                    id="hasSyncSeatingCharts"
                                    name="hasSyncSeatingCharts"
                                />
                            }
                            label={t('configuration.enable-sync-seating-charts')}
                        />
                    </FormGroup>
                    <FormGroup>
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={featureSettings.options.hasTabScreenshare}
                                    onChange={handleUpdateOptionsBool}
                                    id="hasTabScreenshare"
                                    name="hasTabScreenshare"
                                />
                            }
                            label={t('configuration.hasTabScreenshare') as string}
                        />
                    </FormGroup>
                    <FormGroup>
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={featureSettings.options.allowTabLocking}
                                    onChange={handleUpdateOptionsBool}
                                    id="allowTabLocking"
                                    name="allowTabLocking"
                                />
                            }
                            label={t('configuration.allow-tab-locking') as string}
                        />
                    </FormGroup>
                    <FormGroup>
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={featureSettings.enableLogAutoUpload}
                                    onChange={handleFeatureSettingsChange}
                                    id="enableLogAutoUpload"
                                    name="enableLogAutoUpload"
                                />
                            }
                            label={t('user-access.enable-log-auto-upload') as string}
                        />
                    </FormGroup>
                    {!isTeams && (
                        <FormGroup>
                            <AdminControlCheckbox
                                control={
                                    <Checkbox
                                        color="primary"
                                        checked={featureSettings.recordingLegacyZoomList}
                                        onChange={handleFeatureSettingsChange}
                                        id="recordingLegacyZoomList"
                                        name="recordingLegacyZoomList"
                                    />
                                }
                                label={t('configuration.recordings.zoom-legacy') as string}
                            />
                        </FormGroup>
                    )}
                    <FormGroup>
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={featureSettings.dataPortalAdmin}
                                    onChange={handleFeatureSettingsChange}
                                    id="dataPortalAdmin"
                                    name="dataPortalAdmin"
                                />
                            }
                            label={t('configuration.data-portal-admin') as string}
                        />
                    </FormGroup>
                    <FormGroup>
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={featureSettings.dataPortalBusinessAnalyst}
                                    onChange={handleFeatureSettingsChange}
                                    id="dataPortalBusinessAnalyst"
                                    name="dataPortalBusinessAnalyst"
                                />
                            }
                            label={t('configuration.data-portal-business-analyst') as string}
                        />
                    </FormGroup>
                    <FormGroup>
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={featureSettings.dataPortalClassBusinessAnalyst}
                                    onChange={handleFeatureSettingsChange}
                                    id="dataPortalClassBusinessAnalyst"
                                    name="dataPortalClassBusinessAnalyst"
                                />
                            }
                            label={t('configuration.data-portal-class-business-analyst') as string}
                        />
                    </FormGroup>
                    <FormGroup>
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={featureSettings.dataPortalInstructor}
                                    onChange={handleFeatureSettingsChange}
                                    id="dataPortalInstructor"
                                    name="dataPortalInstructor"
                                />
                            }
                            label={t('configuration.data-portal-instructor') as string}
                        />
                    </FormGroup>
                    <FormGroup>
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={featureSettings.dataPortalStudent}
                                    onChange={handleFeatureSettingsChange}
                                    id="dataPortalStudent"
                                    name="dataPortalStudent"
                                />
                            }
                            label={t('configuration.data-portal-student') as string}
                        />
                    </FormGroup>
                    <FormGroup>
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={featureSettings.options.forceWebAsChromebook}
                                    onChange={handleUpdateOptionsBool}
                                    id="forceWebAsChromebook"
                                    name="forceWebAsChromebook"
                                />
                            }
                            label={t('configuration.force-web-as-chromebook') as string}
                            hint={t('configuration.force-web-as-chromebook-hint') as string}
                        />
                    </FormGroup>
                    <FormGroup>
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={featureSettings.options.hasNextGenFiles}
                                    onChange={handleUpdateOptionsBool}
                                    id="hasNextGenFiles"
                                    name="hasNextGenFiles"
                                />
                            }
                            label={t('configuration.hasNextGenFiles') as string}
                        />
                    </FormGroup>
                    <FormGroup>
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={featureSettings.options.hasTabScreenshareShareAudio}
                                    onChange={handleUpdateOptionsBool}
                                    id="hasTabScreenshareShareAudio"
                                    name="hasTabScreenshareShareAudio"
                                />
                            }
                            label={t('configuration.hasTabScreenshareShareAudio') as string}
                        />
                    </FormGroup>
                    <FormGroup>
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={featureSettings.options.hasTabScreenshareAnnotation}
                                    onChange={handleUpdateOptionsBool}
                                    id="hasTabScreenshareAnnotation"
                                    name="hasTabScreenshareAnnotation"
                                />
                            }
                            label={t('configuration.hasTabScreenshareAnnotation') as string}
                        />
                    </FormGroup>
                    <FormGroup>
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={featureSettings.options.hasNextGenSidebar}
                                    onChange={handleUpdateOptionsBool}
                                    id="hasNextGenSidebar"
                                    name="hasNextGenSidebar"
                                />
                            }
                            label={t('configuration.hasNextGenSidebar') as string}
                        />
                    </FormGroup>
                    <FormGroup>
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={featureSettings.options.hasNextGenChat}
                                    onChange={handleUpdateOptionsBool}
                                    id="hasNextGenChat"
                                    name="hasNextGenChat"
                                />
                            }
                            label={t('configuration.enable-next-gen-chat') as string}
                        />
                    </FormGroup>
                    <OverrideControls
                        overrideName="allowNextGenMobile"
                        overrideValue={featureSettings.overrides?.allowNextGenMobile ?? ClassLevelOverrideOptions.NoOne}
                        handleUpdateOverrides={handleUpdateOverrides}
                        labelText={t('whoCanOverride')}
                        tooltipText={t('whoCanOverrideTooltip')}
                    />
                    <FormGroup>
                        <AdminControlCheckbox
                            key="allowLmsGroupBreakouts"
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={featureSettings.options.allowLmsGroupBreakouts}
                                    onChange={handleUpdateOptionsBool}
                                    id="allowLmsGroupBreakouts"
                                    name="allowLmsGroupBreakouts"
                                />
                            }
                            label={t('configuration.enable-lms-group-bors')}
                            hint={t('configuration.enable-lms-group-bors-helper-text')}
                        />
                    </FormGroup>
                    <FormGroup>
                        <AdminControlCheckbox
                            key="enablePerSessionAttendanceOptions"
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={featureSettings.options.enablePerSessionAttendanceOptions}
                                    onChange={handleUpdateOptionsBool}
                                    id="enablePerSessionAttendanceOptions"
                                    name="enablePerSessionAttendanceOptions"
                                />
                            }
                            label={t('configuration.attendance-config.enable-per-session-attendance-options')}
                            hint={t('configuration.attendance-config.enable-per-session-attendance-options-hint')}
                        />
                    </FormGroup>

                    {isTeams && (
                        <>
                            <FormGroup>
                                <AdminControlCheckbox
                                    control={
                                        <Checkbox
                                            checked={featureSettings.options.hasTelephony}
                                            onChange={handleUpdateOptionsBool}
                                            id="hasTelephony"
                                            name="hasTelephony"
                                            disabled={!hasAzurePhoneNumbers}
                                        />
                                    }
                                    label={t('configuration.allowTelephony')}
                                    hint={t('configuration.allowTelephonyHint')}
                                />
                                {hasAzurePhoneNumbers && (
                                    <OverrideControls
                                        overrideName="hasTelephony"
                                        overrideValue={
                                            featureSettings.overrides?.hasTelephony ?? ClassLevelOverrideOptions.NoOne
                                        }
                                        handleUpdateOverrides={handleUpdateOverrides}
                                        labelText={t('whoCanOverride')}
                                        tooltipText={t('whoCanOverrideTooltip')}
                                    />
                                )}
                            </FormGroup>
                        </>
                    )}
                </StyledFormGroupContainer>
            </StyledFormGroupContainer>

            {Boolean(Object.keys(errors).length) && (
                <Alert sx={{ mb: 3 }} severity="error">
                    {t('configuration.errors-present')}
                </Alert>
            )}
            <SaveButton isSaving={isSavingSettingsData} disabled={isSavingDisabled} onClick={errorCheckSave} />

            <Spacer />

            <SuperAdminTools />
        </>
    );
};

export default SuperAdmin;
